import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';

const NavbarContainer = styled.nav`
  background: rgba(10, 10, 10, 0.15);
  backdrop-filter: blur(10px);
  padding: 1.2rem 3rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(10, 10, 10, 0.2);
  }

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const Logo = styled(Link)`
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  text-decoration: none;
  display: flex;
  align-items: center;

  span {
    color: var(--primary-color);
    font-family: 'Caveat', cursive;
    font-size: 2.4rem;
    font-weight: 400;
    margin-left: 6rem;
    text-shadow: 0 0 20px rgba(255, 51, 51, 0.3);
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
      text-shadow: 0 0 30px rgba(255, 51, 51, 0.5);
    }
    
    @media (max-width: 768px) {
      font-size: 1.8rem;
      margin-left: 0.5rem;
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1001;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-right: 3rem;

  @media (max-width: 768px) {
    margin-right: 0;
    position: fixed;
    top: 0;
    right: ${props => (props.isOpen ? '0' : '-100%')};
    width: 70%;
    height: 100vh;
    background: rgba(10, 10, 10, 0.95);
    backdrop-filter: blur(10px);
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 4rem;
    transition: right 0.3s ease;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.3);
  }
`;

const NavLink = styled(Link)`
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0.5rem;
  text-decoration: none;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  position: relative;
  transition: all 0.2s ease;
  padding: 0.5rem 1rem;
  
  &:hover {
    color: #fff;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: var(--primary-color);
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.3s ease;
  }
  
  &:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const ExternalLink = styled.a`
  color: var(--primary-color);
  margin: 0 0.5rem;
  text-decoration: none;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  padding: 0.5rem 1.2rem;
  background: rgba(255, 51, 51, 0.1);
  border: none;
  border-radius: 6px;
  position: relative;
  isolation: isolate;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(45deg, var(--primary-color), var(--accent-color));
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 6px;
    z-index: -1;
  }
  
  &:hover {
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 10px 20px -10px var(--primary-color);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const CloseButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  position: absolute;
  top: 1rem;
  right: 1rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Navbar = ({ serverName, navigation, externalLinks }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  return (
    <NavbarContainer scrolled={scrolled}>
      <Logo to="/">
        <span>{serverName}</span>
      </Logo>
      
      {!isOpen && (
        <MenuButton onClick={toggleMenu}>
          <FontAwesomeIcon icon={faBars} />
        </MenuButton>
      )}
      
      <NavLinks isOpen={isOpen}>
        <CloseButton onClick={closeMenu}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        {navigation.map((item, index) => (
          <NavLink key={index} to={item.path} onClick={closeMenu}>
            {item.name}
          </NavLink>
        ))}
        {externalLinks && externalLinks.map((item, index) => (
          <ExternalLink 
            key={index} 
            href={item.url} 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={closeMenu}
          >
            {item.name}
          </ExternalLink>
        ))}
      </NavLinks>
    </NavbarContainer>
  );
};

export default Navbar;
